import { FabricObject, Group } from 'fabric';

export type Bbox = { left: number; top: number; width: number; height: number };

/**
 * Rasterizes a Fabric object into a transparent canvas of `bbox * scale`
 * physical pixels. The output captures the bbox region of canvas-space:
 * the subject's pixels land at their position relative to `bbox.left/top`,
 * so passing the subject's own bounding rect preserves all overflow.
 *
 *   - `scale > 1` supersamples to match the live canvas's Retina quality.
 *   - Object caching is disabled across the subtree so the output reads
 *     from source primitives instead of Fabric's softening cache bitmaps.
 */
export function rasterizeFabricObject(obj: FabricObject, bbox: Bbox, scale: number = 1): HTMLCanvasElement {
  const out = document.createElement('canvas');
  out.width = Math.round(bbox.width * scale);
  out.height = Math.round(bbox.height * scale);
  const ctx = out.getContext('2d');
  if (!ctx) return out;

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  if (scale !== 1) ctx.scale(scale, scale);
  // Shift world-coords so `(bbox.left, bbox.top)` lands at the texture origin.
  ctx.translate(-bbox.left, -bbox.top);

  withCachingDisabled(obj, () => obj.render(ctx));
  return out;
}

function withCachingDisabled<T>(root: FabricObject, fn: () => T): T {
  const previous = new Map<FabricObject, boolean | undefined>();
  const disable = (o: FabricObject) => {
    previous.set(o, o.objectCaching);
    o.objectCaching = false;
    (o as Group).getObjects?.().forEach(disable);
  };
  disable(root);
  try {
    return fn();
  } finally {
    previous.forEach((prev, o) => {
      o.objectCaching = prev ?? true;
    });
  }
}
